import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
} from "@mui/material";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  DateRangePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Download,
  FilterList,
  PersonAdd,
  Assessment,
  Event,
  Group,
  Person,
  TrendingUp,
  TrendingDown,
  Schedule,
  Clear,
  FileDownload,
  Print,
  Refresh,
  Settings,
  Dashboard,
} from "@mui/icons-material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken, base_url } from "../../config/config";

dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

const Attendance = () => {
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Form Data
  const [formData, setFormData] = useState({
    user: "",
    StartTime: "",
    endTime: "",
    Date: "",
  });

  // Advanced Filters
  const [filters, setFilters] = useState({
    employees: [],
    dateRange: [null, null],
    filterType: "all", // all, abwesend
    quickFilter: "", // today, week, month, custom
  });

  // Export Options
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: "excel", // excel, pdf
    includeStatistics: true,
    groupByEmployee: false,
    includeSummary: true,
    includeOvertimeAnalysis: true,
  });
  const [exportFilters, setExportFilters] = useState({
    employees: [],
    dateRange: [null, null],
  });

  const specialDates = [
    "2024-03-28",
    "2024-05-08",
    "2024-12-25",
    "2024-12-26",
    "2025-01-01",
  ];

  const holidays = {
    "2024-12-25": "Christmas",
    "2024-12-26": "Boxing Day",
    "2025-01-01": "New Year",
    "2024-03-28": "Good Friday",
    "2024-05-08": "Victory Day",
  };

  // Helper function to get employee name
  const getEmployeeName = (employee) => {
    if (typeof employee === "string") return employee;
    return employee.name || employee.fullName || employee.username || "";
  };

  // Helper function to get employee list for display
  const getEmployeeList = () => {
    return employees.map((emp) => getEmployeeName(emp));
  };

  // Fetch Data
  useEffect(() => {
    fetchData();
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await axios.get(`${base_url}/v1/getAllEmployees`);
      setEmployees(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching employees:", error);
      showSnackbar("Fehler beim Laden der Mitarbeiter", "error");
      setError(error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${base_url}/v1/getDailyAttendance`);
      setData(response.data.data);
      setError(null);
    } catch (err) {
      setError(err);
      showSnackbar("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Date Calculations
  const isWeekend = (date) => {
    const dayOfWeek = dayjs(date).day();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) or Saturday (6)
  };

  const isSpecialDay = (date) => {
    const dayOfWeek = dayjs(date).day();
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    return dayOfWeek === 5 || specialDates.includes(formattedDate);
  };

  const isHoliday = (date) => {
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    return holidays[formattedDate] || null;
  };

  const calculateTotalTime = (start, end, date) => {
    const recordDate = dayjs(date).startOf("day");
    const today = dayjs().startOf("day");
    const isToday = recordDate.isSame(today, "day");

    if (!start && !end) {
      return "Abwesend";
    }

    if (isToday && start && !end) {
      return "Arbeitet noch";
    }

    if (!start || !end) {
      return "Unvollständig";
    }

      if (start === "Abwesend" && end === "Abwesend") {
        return "Abwesend";
      }

      let startTime = dayjs(start, ["HH:mm", "HH:mm:ss"]);
      let endTime = dayjs(end, ["HH:mm", "HH:mm:ss"]);

    if (startTime.isValid() && endTime.isValid()) {
      const endCap = dayjs("15:00", "HH:mm");
      if (endTime.isAfter(endCap)) {
        endTime = endCap;
      }
    }

    if (startTime.isValid() && endTime.isValid() && endTime.isAfter(startTime)) {
      let totalDuration = dayjs.duration(endTime.diff(startTime));

      // Stop the timer after 8 hours - elapsed work time is capped at 8 hours
      if (totalDuration.asHours() > 8) {
        totalDuration = dayjs.duration(8, "hours");
      }

      const reducedDuration = totalDuration.subtract(45, "minute");


      if (reducedDuration.asMinutes() < 0) {
        return "Ungültige Zeit";
      }
      return reducedDuration;
    }
    return "Ungültige Zeit";
  };

  const getCappedEndTime = (end) => {
    if (!end) return end;
    const endTime = dayjs(end, ["HH:mm", "HH:mm:ss"]);
    if (!endTime.isValid()) return end;
    const endCap = dayjs("15:00", "HH:mm");
    return endTime.isAfter(endCap) ? endCap.format("HH:mm") : endTime.format("HH:mm");
  };

  // Quick Date Filters
  const applyQuickFilter = (filterType) => {
    const today = dayjs();
    let startDate, endDate;

    switch (filterType) {
      case "today":
        startDate = today.startOf("day");
        endDate = today.endOf("day");
        break;
      case "week":
        startDate = today.startOf("isoWeek");
        endDate = today.endOf("isoWeek");
        break;
      case "month":
        startDate = today.startOf("month");
        endDate = today.endOf("month");
        break;
      case "lastMonth":
        startDate = today.subtract(1, "month").startOf("month");
        endDate = today.subtract(1, "month").endOf("month");
        break;
      default:
        startDate = null;
        endDate = null;
    }

    setFilters({
      ...filters,
      dateRange: [startDate, endDate],
      quickFilter: filterType,
    });
  };

  // Filter Data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Employee Filter
      if (
        filters.employees.length > 0 &&
        !filters.employees.includes(item.user)
      ) {
        return false;
      }

      // Date Range Filter
      if (filters.dateRange[0] && filters.dateRange[1]) {
        const itemDate = dayjs(item.Date);
        if (
          !itemDate.isBetween(
            filters.dateRange[0],
            filters.dateRange[1],
            null,
            "[]",
          )
        ) {
          return false;
        }
      }

      // Type Filter
      switch (filters.filterType) {
        case "abwesend":
          return (
            !item.StartTime &&
            !item.endTime &&
            item.AbwesendType !== "gleittag" &&
            item.AbwesendType !== "urlaub-uberstunden"
          );
        default:
          return true;
      }
    });
  }, [data, filters]);

  // Statistics
  const statistics = useMemo(() => {
    const stats = {
      totalRecords: filteredData.length,
      totalEmployees: new Set(filteredData.map(d => d.user)).size,
      totalWorkingHours: dayjs.duration(0),
      absentDays: 0,
      averageWorkingHours: null,
      employeeStats: {},
    };

    filteredData.forEach((record) => {
      const totalTime = calculateTotalTime(record.StartTime, record.endTime, record.Date);

      // Initialize employee stats if not exists
      if (!stats.employeeStats[record.user]) {
        stats.employeeStats[record.user] = {
          totalDays: 0,
          workDays: 0,
          absentDays: 0,
          totalHours: dayjs.duration(0),
        };
      }

      stats.employeeStats[record.user].totalDays++;

      if (totalTime === "Abwesend") {
        stats.absentDays++;
        stats.employeeStats[record.user].absentDays++;
      } else if (dayjs.isDuration(totalTime)) {
        stats.totalWorkingHours = stats.totalWorkingHours.add(totalTime);
        stats.employeeStats[record.user].workDays++;
          stats.employeeStats[record.user].totalHours =
            stats.employeeStats[record.user].totalHours.add(totalTime);
      }
    });

    const workingRecords = filteredData.filter((r) => {
      const time = calculateTotalTime(r.StartTime, r.endTime, r.Date);
      return dayjs.isDuration(time);
    }).length;

    if (workingRecords > 0) {
      stats.averageWorkingHours = dayjs.duration(
        stats.totalWorkingHours.asMinutes() / workingRecords,
        "minutes",
      );
    }

    return stats;
  }, [filteredData]);

  // Export Functions
  const exportData = () => {
    const dataToExport = getExportFilteredData();
    const exportStats = getExportStats(dataToExport);

    const exportableData = dataToExport
      .sort((a, b) => {
        const nameCompare = (a.user || "").localeCompare(b.user || "");
        if (nameCompare !== 0) return nameCompare;
        return dayjs(a.Date).isBefore(dayjs(b.Date)) ? -1 : 1;
      })
      .map((item) => {
        const { überstunden, minusstunden, workedMin, officialWorkedMin, type } = calculateOvertimeData(item);

        // Arbeitszeit = official portion (start → 15:00 minus 45min break); overtime shown separately
        let arbeitszeit;
        if (type === "absent") arbeitszeit = getStatus(null, item);
        else if (type === "gleittag") arbeitszeit = "Gleittag";
        else if (type === "urlaub-uberstunden") arbeitszeit = "Überstunden-Urlaub";
        else if (type === "no-data" || type === "incomplete" || type === "invalid") arbeitszeit = "-";
        else if (type === "weekend") arbeitszeit = formatMinutes(workedMin);
        else arbeitszeit = formatMinutes(officialWorkedMin);

        // Status still uses the UI logic for correct absence labels
        const uiTotalTime = calculateTotalTime(item.StartTime, item.endTime, item.Date);

        return {
          Mitarbeiter: item.user,
          Datum: dayjs(item.Date).format("DD.MM.YYYY"),
          Wochentag: dayjs(item.Date).format("dddd"),
          // Actual raw StartTime and endTime from database — no modifications
          Startzeit: item.StartTime || "-",
          Endzeit: item.endTime || "-",
          Arbeitszeit: arbeitszeit,
          Überstunden: überstunden > 0 ? formatMinutes(überstunden) : "-",
          Minusstunden: minusstunden > 0 ? formatMinutes(minusstunden) : "-",
          Status: getStatus(uiTotalTime, item),
          Feiertag: isHoliday(item.Date) || "-",
        };
      });

    switch (exportOptions.format) {
      case "excel":
        exportToExcel(exportableData, exportStats);
        break;
      case "pdf":
        exportToPDF(exportableData, exportStats);
        break;
    }

    setExportDialogOpen(false);
    showSnackbar("Export erfolgreich!");
  };

  const exportToExcel = (exportableData, exportStats) => {
    const wb = XLSX.utils.book_new();

    const periodLabel =
      exportFilters.dateRange[0] && exportFilters.dateRange[1]
        ? `${exportFilters.dateRange[0].format("DD.MM.YYYY")} - ${exportFilters.dateRange[1].format("DD.MM.YYYY")}`
        : "Alle Daten";

    const companyInfo = [
      ["ANWESENHEITSBERICHT — ÜBERSTUNDEN & MINUSSTUNDEN"],
      [""],
      ["Erstellt am:", dayjs().format("DD.MM.YYYY HH:mm")],
      ["Zeitraum:", periodLabel],
      ["Mitarbeiter:", exportFilters.employees.length > 0 ? exportFilters.employees.join(", ") : "Alle"],
      [""],
    ];

    const summaryRows = [
      ["ZUSAMMENFASSUNG"],
      [""],
      ["Gesamte Einträge:", exportableData.length],
      ["Anzahl Mitarbeiter:", Object.keys(exportStats.empStats).length],
      ["Gesamte Überstunden:", formatMinutes(exportStats.totalÜberstunden)],
      ["Gesamte Minusstunden:", formatMinutes(exportStats.totalMinusstunden)],
      ["Nettosaldo:", (exportStats.netBalance >= 0 ? "+" : "") + formatMinutes(Math.abs(exportStats.netBalance))],
      ["Gesamte Arbeitszeit:", formatMinutes(exportStats.totalWorkedMin)],
      [""],
    ];

    const TABLE_HEADERS = [
      "Mitarbeiter", "Datum", "Wochentag", "Startzeit", "Endzeit",
      "Arbeitszeit", "Überstunden", "Minusstunden", "Status", "Feiertag",
    ];
    const COL_WIDTHS = [
      { wch: 22 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 10 },
      { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
    ];

    if (!exportOptions.groupByEmployee) {
      const dataArray = exportableData.map((item) => [
        item.Mitarbeiter, item.Datum, item.Wochentag, item.Startzeit, item.Endzeit,
        item.Arbeitszeit, item.Überstunden, item.Minusstunden, item.Status, item.Feiertag,
      ]);

      const allData = [...companyInfo, ...summaryRows, TABLE_HEADERS, ...dataArray];
      const ws = XLSX.utils.aoa_to_sheet(allData);
      ws["!cols"] = COL_WIDTHS;
      XLSX.utils.book_append_sheet(wb, ws, "Anwesenheitsbericht");
    } else {
      // Summary sheet first
      const summarySheetData = [
        ...companyInfo,
        ...summaryRows,
        ["MITARBEITER-ÜBERSICHT"],
        [""],
        ["Mitarbeiter", "Arbeitstage", "Abwesenheit", "Gesamtarbeitszeit", "Überstunden", "Minusstunden", "Nettosaldo"],
      ];

      Object.entries(exportStats.empStats).forEach(([employee, stats]) => {
        const net = stats.netBalance;
        summarySheetData.push([
          employee,
          stats.workDays,
          stats.absentDays,
          formatMinutes(stats.totalWorkedMin),
          formatMinutes(stats.überstunden),
          formatMinutes(stats.minusstunden),
          (net >= 0 ? "+" : "") + formatMinutes(Math.abs(net)),
        ]);
      });

      const summaryWs = XLSX.utils.aoa_to_sheet(summarySheetData);
      summaryWs["!cols"] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, summaryWs, "Zusammenfassung");

      // Individual employee sheets
      const uniqueEmployees = [...new Set(exportableData.map((d) => d.Mitarbeiter))];
      uniqueEmployees.forEach((employee) => {
        const empData = exportableData.filter((d) => d.Mitarbeiter === employee);
        if (empData.length === 0) return;

        const empStats = exportStats.empStats[employee] || {};
        const net = empStats.netBalance || 0;

        const employeeSheet = [
          [employee.toUpperCase()],
          [""],
          ["Zeitraum:", periodLabel],
          [""],
          ["STATISTIKEN"],
          ["Arbeitstage:", empStats.workDays || 0],
          ["Abwesenheitstage:", empStats.absentDays || 0],
          ["Gesamte Arbeitszeit:", formatMinutes(empStats.totalWorkedMin || 0)],
          ["Überstunden:", formatMinutes(empStats.überstunden || 0)],
          ["Minusstunden:", formatMinutes(empStats.minusstunden || 0)],
          ["Nettosaldo:", (net >= 0 ? "+" : "") + formatMinutes(Math.abs(net))],
          [""],
          ["DETAILLIERTE AUFZEICHNUNGEN"],
          [""],
          ["Datum", "Wochentag", "Startzeit", "Endzeit", "Arbeitszeit", "Überstunden", "Minusstunden", "Status"],
        ];

        empData.forEach((record) => {
          employeeSheet.push([
            record.Datum, record.Wochentag, record.Startzeit, record.Endzeit,
            record.Arbeitszeit, record.Überstunden, record.Minusstunden, record.Status,
          ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(employeeSheet);
        ws["!cols"] = [
          { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 10 },
          { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, employee.substring(0, 31));
      });
    }

    // Overtime Analysis sheet
    if (exportOptions.includeOvertimeAnalysis) {
      const overtimeSheetData = [
        ["ÜBERSTUNDEN & MINUSSTUNDEN ANALYSE"],
        [""],
        ["Erstellt am:", dayjs().format("DD.MM.YYYY HH:mm")],
        ["Zeitraum:", periodLabel],
        ["Standard pro Tag:", formatMinutes(STANDARD_MINUTES_PER_DAY)],
        [""],
        ["MITARBEITER-BILANZ"],
        [""],
        ["Mitarbeiter", "Arbeitstage", "Abwesenheit", "Gesamtarbeitszeit", "Überstunden (+)", "Minusstunden (-)", "Nettosaldo"],
      ];

      Object.entries(exportStats.empStats)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([employee, stats]) => {
          const net = stats.netBalance;
          overtimeSheetData.push([
            employee,
            stats.workDays,
            stats.absentDays,
            formatMinutes(stats.totalWorkedMin),
            formatMinutes(stats.überstunden),
            formatMinutes(stats.minusstunden),
            (net >= 0 ? "+" : "") + formatMinutes(Math.abs(net)),
          ]);
        });

      overtimeSheetData.push(
        [""],
        ["GESAMTBILANZ"],
        [
          "GESAMT",
          "",
          "",
          formatMinutes(exportStats.totalWorkedMin),
          formatMinutes(exportStats.totalÜberstunden),
          formatMinutes(exportStats.totalMinusstunden),
          (exportStats.netBalance >= 0 ? "+" : "") + formatMinutes(Math.abs(exportStats.netBalance)),
        ],
      );

      const overtimeWs = XLSX.utils.aoa_to_sheet(overtimeSheetData);
      overtimeWs["!cols"] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, overtimeWs, "Überstunden-Analyse");
    }

    // Statistics sheet
    if (exportOptions.includeStatistics) {
      const statsData = [
        ["DETAILLIERTE STATISTIKEN"],
        [""],
        ["Erstellt am:", dayjs().format("DD.MM.YYYY HH:mm")],
        [""],
        ["GESAMTÜBERSICHT"],
        ["Metrik", "Wert"],
        ["Gesamte Einträge", exportableData.length],
        ["Mitarbeiter", Object.keys(exportStats.empStats).length],
        ["Gesamte Arbeitszeit", formatMinutes(exportStats.totalWorkedMin)],
        ["Gesamte Überstunden", formatMinutes(exportStats.totalÜberstunden)],
        ["Gesamte Minusstunden", formatMinutes(exportStats.totalMinusstunden)],
        ["Nettosaldo", (exportStats.netBalance >= 0 ? "+" : "") + formatMinutes(Math.abs(exportStats.netBalance))],
        [""],
        ["MITARBEITER-DETAILS"],
        ["Mitarbeiter", "Arbeitstage", "Abwesenheit", "Arbeitszeit", "Überstunden", "Minusstunden", "Saldo"],
      ];

      Object.entries(exportStats.empStats).forEach(([employee, stats]) => {
        const net = stats.netBalance;
        statsData.push([
          employee,
          stats.workDays,
          stats.absentDays,
          formatMinutes(stats.totalWorkedMin),
          formatMinutes(stats.überstunden),
          formatMinutes(stats.minusstunden),
          (net >= 0 ? "+" : "") + formatMinutes(Math.abs(net)),
        ]);
      });

      const statsWs = XLSX.utils.aoa_to_sheet(statsData);
      statsWs["!cols"] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, statsWs, "Statistiken");
    }

    const fileName = `Anwesenheitsbericht_${dayjs().format("YYYY-MM-DD_HHmm")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportToPDF = (exportableData, exportStats) => {
    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let currentY = 15;

    const periodLabel =
      exportFilters.dateRange[0] && exportFilters.dateRange[1]
        ? `${exportFilters.dateRange[0].format("DD.MM.YYYY")} - ${exportFilters.dateRange[1].format("DD.MM.YYYY")}`
        : "Vollständiger Zeitraum";

    const drawPageFooter = () => {
      const total = doc.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(`Seite ${i} von ${total}`, pageWidth / 2, pageHeight - 7, { align: "center" });
        doc.text(`Erstellt am: ${dayjs().format("DD.MM.YYYY HH:mm")}`, pageWidth - 15, pageHeight - 7, { align: "right" });
      }
    };

    // ── Page 1: Header ──────────────────────────────────────────────────────
    doc.setFillColor(30, 90, 150);
    doc.rect(0, 0, pageWidth, 32, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, "bold");
    doc.text("ANWESENHEITSBERICHT — ÜBERSTUNDEN & MINUSSTUNDEN", pageWidth / 2, 13, { align: "center" });

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.text(periodLabel, pageWidth / 2, 22, { align: "center" });
    if (exportFilters.employees.length > 0) {
      doc.setFontSize(9);
      doc.text(`Mitarbeiter: ${exportFilters.employees.join(", ")}`, pageWidth / 2, 29, { align: "center" });
    }

    currentY = 40;
    doc.setTextColor(0, 0, 0);

    // ── Summary box ─────────────────────────────────────────────────────────
    if (exportOptions.includeSummary) {
      const boxH = 52;
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(15, currentY, pageWidth - 30, boxH, 3, 3, "F");
      doc.setDrawColor(180, 200, 220);
      doc.roundedRect(15, currentY, pageWidth - 30, boxH, 3, 3, "S");

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.setTextColor(30, 90, 150);
      doc.text("ZUSAMMENFASSUNG", 22, currentY + 9);
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");

      const col1x = 22;
      const col2x = pageWidth / 2 - 10;
      const col3x = pageWidth - 85;

      // Col 1
      doc.setFont(undefined, "bold");
      doc.text("Einträge:", col1x, currentY + 20);
      doc.setFont(undefined, "normal");
      doc.text(String(exportableData.length), col1x + 30, currentY + 20);

      doc.setFont(undefined, "bold");
      doc.text("Mitarbeiter:", col1x, currentY + 28);
      doc.setFont(undefined, "normal");
      doc.text(String(Object.keys(exportStats.empStats).length), col1x + 30, currentY + 28);

      doc.setFont(undefined, "bold");
      doc.text("Arbeitszeit:", col1x, currentY + 36);
      doc.setFont(undefined, "normal");
      doc.text(formatMinutes(exportStats.totalWorkedMin), col1x + 30, currentY + 36);

      // Col 2: Überstunden
      doc.setFillColor(230, 247, 230);
      doc.roundedRect(col2x, currentY + 12, 60, 28, 2, 2, "F");
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      doc.setTextColor(30, 120, 30);
      doc.text("ÜBERSTUNDEN", col2x + 30, currentY + 20, { align: "center" });
      doc.setFontSize(13);
      doc.text("+ " + formatMinutes(exportStats.totalÜberstunden), col2x + 30, currentY + 32, { align: "center" });

      // Col 3: Minusstunden
      doc.setFillColor(253, 235, 235);
      doc.roundedRect(col3x, currentY + 12, 60, 28, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(180, 30, 30);
      doc.text("MINUSSTUNDEN", col3x + 30, currentY + 20, { align: "center" });
      doc.setFontSize(13);
      doc.text("- " + formatMinutes(exportStats.totalMinusstunden), col3x + 30, currentY + 32, { align: "center" });

      // Net balance
      const netBal = exportStats.netBalance;
      doc.setTextColor(netBal >= 0 ? 30 : 180, netBal >= 0 ? 120 : 30, 30);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(
        `Nettosaldo: ${netBal >= 0 ? "+" : ""}${formatMinutes(Math.abs(netBal))}`,
        pageWidth - 20,
        currentY + 47,
        { align: "right" },
      );

      doc.setTextColor(0, 0, 0);
      currentY += boxH + 8;
    }

    doc.setFont(undefined, "normal");

    // ── Main data table ─────────────────────────────────────────────────────
    const tableColumns = [
      { header: "Mitarbeiter", dataKey: "Mitarbeiter" },
      { header: "Datum", dataKey: "Datum" },
      { header: "Tag", dataKey: "Wochentag" },
      { header: "Start", dataKey: "Startzeit" },
      { header: "Ende", dataKey: "Endzeit" },
      { header: "Arbeitszeit", dataKey: "Arbeitszeit" },
      { header: "Überstunden", dataKey: "Überstunden" },
      { header: "Minusstunden", dataKey: "Minusstunden" },
      { header: "Status", dataKey: "Status" },
    ];

    const commonTableStyles = {
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [30, 90, 150], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        Mitarbeiter: { fontStyle: "bold" },
        Überstunden: { textColor: [30, 120, 30], fontStyle: "bold" },
        Minusstunden: { textColor: [180, 30, 30], fontStyle: "bold" },
      },
    };

    if (exportOptions.groupByEmployee) {
      const groupedData = {};
      exportableData.forEach((item) => {
        if (!groupedData[item.Mitarbeiter]) groupedData[item.Mitarbeiter] = [];
        groupedData[item.Mitarbeiter].push(item);
      });

      Object.entries(groupedData).forEach(([employee, records], index) => {
        if (index > 0) { doc.addPage(); currentY = 15; }

        const empStats = exportStats.empStats[employee] || {};
        const net = empStats.netBalance || 0;

        // Employee section header
        doc.setFillColor(240, 244, 248);
        doc.rect(15, currentY, pageWidth - 30, 14, "F");
        doc.setDrawColor(30, 90, 150);
        doc.rect(15, currentY, pageWidth - 30, 14, "S");
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.setTextColor(30, 90, 150);
        doc.text(employee, 20, currentY + 9);

        // Employee stats inline
        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        doc.setTextColor(60, 60, 60);
        doc.text(`Arbeitstage: ${empStats.workDays || 0}`, pageWidth * 0.4, currentY + 5);
        doc.text(`Abwesenheit: ${empStats.absentDays || 0}`, pageWidth * 0.4, currentY + 11);
        doc.text(`Arbeitszeit: ${formatMinutes(empStats.totalWorkedMin || 0)}`, pageWidth * 0.55, currentY + 5);
        doc.setTextColor(30, 120, 30);
        doc.text(`ÜS: +${formatMinutes(empStats.überstunden || 0)}`, pageWidth * 0.7, currentY + 5);
        doc.setTextColor(180, 30, 30);
        doc.text(`MS: -${formatMinutes(empStats.minusstunden || 0)}`, pageWidth * 0.7, currentY + 11);
        doc.setTextColor(net >= 0 ? 30 : 180, net >= 0 ? 120 : 30, 30);
        doc.setFont(undefined, "bold");
        doc.text(`Saldo: ${net >= 0 ? "+" : ""}${formatMinutes(Math.abs(net))}`, pageWidth * 0.84, currentY + 8);

        doc.setTextColor(0, 0, 0);
        currentY += 18;

        const empCols = tableColumns.slice(1); // no Mitarbeiter column per-sheet
        doc.autoTable({
          columns: empCols,
          body: records,
          startY: currentY,
          theme: "grid",
          ...commonTableStyles,
          columnStyles: {
            ...commonTableStyles.columnStyles,
            Mitarbeiter: undefined,
          },
        });

        currentY = doc.lastAutoTable.finalY + 8;
      });
    } else {
      doc.autoTable({
        columns: tableColumns,
        body: exportableData,
        startY: currentY,
        theme: "grid",
        ...commonTableStyles,
      });
    }

    // ── Overtime Analysis page ───────────────────────────────────────────────
    if (exportOptions.includeOvertimeAnalysis && Object.keys(exportStats.empStats).length > 0) {
      doc.addPage();

      doc.setFillColor(30, 90, 150);
      doc.rect(0, 0, pageWidth, 25, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text("ÜBERSTUNDEN & MINUSSTUNDEN — MITARBEITER-BILANZ", pageWidth / 2, 15, { align: "center" });

      doc.setTextColor(0, 0, 0);

      const summaryBody = Object.entries(exportStats.empStats)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([employee, stats]) => {
          const net = stats.netBalance;
          return {
            name: employee,
            workDays: stats.workDays,
            absentDays: stats.absentDays,
            totalWork: formatMinutes(stats.totalWorkedMin),
            uber: "+ " + formatMinutes(stats.überstunden),
            minus: "- " + formatMinutes(stats.minusstunden),
            net: (net >= 0 ? "+" : "") + formatMinutes(Math.abs(net)),
          };
        });

      doc.autoTable({
        columns: [
          { header: "Mitarbeiter", dataKey: "name" },
          { header: "Arbeitstage", dataKey: "workDays" },
          { header: "Abwesenheit", dataKey: "absentDays" },
          { header: "Gesamtarbeitszeit", dataKey: "totalWork" },
          { header: "Überstunden (+)", dataKey: "uber" },
          { header: "Minusstunden (−)", dataKey: "minus" },
          { header: "Nettosaldo", dataKey: "net" },
        ],
        body: summaryBody,
        startY: 33,
        theme: "striped",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [30, 90, 150], textColor: 255, fontStyle: "bold" },
        columnStyles: {
          name: { fontStyle: "bold" },
          uber: { textColor: [30, 120, 30], fontStyle: "bold" },
          minus: { textColor: [180, 30, 30], fontStyle: "bold" },
          net: { fontStyle: "bold" },
        },
        didParseCell: (data) => {
          if (data.column.dataKey === "net" && data.section === "body") {
            const val = data.cell.raw || "";
            data.cell.styles.textColor = val.startsWith("+") ? [30, 120, 30] : [180, 30, 30];
          }
        },
      });

      // Grand total row
      const finalY = doc.lastAutoTable.finalY + 12;
      const netBal = exportStats.netBalance;
      doc.setFillColor(230, 238, 250);
      doc.rect(15, finalY, pageWidth - 30, 22, "F");
      doc.setDrawColor(30, 90, 150);
      doc.rect(15, finalY, pageWidth - 30, 22, "S");

      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.setTextColor(30, 90, 150);
      doc.text("GESAMTBILANZ", 22, finalY + 8);

      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Gesamtarbeitszeit: ${formatMinutes(exportStats.totalWorkedMin)}`, 22, finalY + 17);

      doc.setTextColor(30, 120, 30);
      doc.text(`+ ${formatMinutes(exportStats.totalÜberstunden)}`, pageWidth * 0.55, finalY + 17, { align: "center" });

      doc.setTextColor(180, 30, 30);
      doc.text(`− ${formatMinutes(exportStats.totalMinusstunden)}`, pageWidth * 0.7, finalY + 17, { align: "center" });

      doc.setTextColor(netBal >= 0 ? 30 : 180, netBal >= 0 ? 120 : 30, 30);
      doc.setFontSize(12);
      doc.text(
        `Nettosaldo: ${netBal >= 0 ? "+" : ""}${formatMinutes(Math.abs(netBal))}`,
        pageWidth - 22,
        finalY + 17,
        { align: "right" },
      );
    }

    drawPageFooter();

    const fileName = `Anwesenheitsbericht_${dayjs().format("YYYY-MM-DD_HHmm")}.pdf`;
    doc.save(fileName);
  };

  // Helper Functions
  const formatDuration = (duration) => {
    if (!duration || !dayjs.isDuration(duration)) {
      return duration || "-";
    }
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours}h ${minutes}m`;
  };

  const formatMinutes = (minutes) => {
    if (minutes === null || minutes === undefined || isNaN(minutes) || minutes < 0) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
  };

  const STANDARD_MINUTES_PER_DAY = 435; // 7h 15min standard working day

  const calculateOvertimeData = (record) => {
    if (record.AbwesendType === "gleittag") {
      return { überstunden: 0, minusstunden: STANDARD_MINUTES_PER_DAY, workedMin: 0, officialWorkedMin: 0, type: "gleittag" };
    }
    if (record.AbwesendType === "urlaub-uberstunden") {
      return { überstunden: 0, minusstunden: STANDARD_MINUTES_PER_DAY, workedMin: 0, officialWorkedMin: 0, type: "urlaub-uberstunden" };
    }
    if (record.Abwesend || record.AbwesendType === "urlaub" || record.AbwesendType === "krank") {
      return { überstunden: 0, minusstunden: 0, workedMin: 0, officialWorkedMin: 0, type: "absent" };
    }

    const weekend = isWeekend(record.Date);

    if (!record.StartTime || !record.endTime ||
        record.StartTime === "Abwesend" || record.endTime === "Abwesend") {
      if (weekend) return { überstunden: 0, minusstunden: 0, workedMin: 0, officialWorkedMin: 0, type: "no-data" };
      return { überstunden: 0, minusstunden: STANDARD_MINUTES_PER_DAY, workedMin: 0, officialWorkedMin: 0, type: "incomplete" };
    }

    const start = dayjs(record.StartTime, ["HH:mm", "HH:mm:ss"]);
    const end = dayjs(record.endTime, ["HH:mm", "HH:mm:ss"]);

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return { überstunden: 0, minusstunden: weekend ? 0 : STANDARD_MINUTES_PER_DAY, workedMin: 0, officialWorkedMin: 0, type: "invalid" };
    }

    if (weekend) {
      // Weekend: all overtime, deduct 45min break
      const workedMin = Math.max(0, end.diff(start, "minute") - 45);
      return { überstunden: workedMin, minusstunden: 0, workedMin, officialWorkedMin: 0, type: "weekend" };
    }

    // Official hours: start → 15:00, minus 45min break
    const officialEndTime = dayjs("15:00", "HH:mm");
    const officialEndCapped = end.isAfter(officialEndTime) ? officialEndTime : end;
    const officialWorkedMin = Math.max(0, officialEndCapped.diff(start, "minute") - 45);

    // Overtime: every minute worked after 15:00 (no additional break deduction)
    const overtimeMinutes = end.isAfter(officialEndTime) ? end.diff(officialEndTime, "minute") : 0;

    const workedMin = officialWorkedMin + overtimeMinutes;
    const überstunden = overtimeMinutes;
    const minusstunden = Math.max(0, STANDARD_MINUTES_PER_DAY - officialWorkedMin);

    return { überstunden, minusstunden, workedMin, officialWorkedMin, type: "work" };
  };

  const getExportFilteredData = () => {
    let result = [...data];

    if (exportFilters.employees.length > 0) {
      result = result.filter((d) => exportFilters.employees.includes(d.user));
    }

    if (exportFilters.dateRange[0] && exportFilters.dateRange[1]) {
      const from = exportFilters.dateRange[0].startOf("day");
      const to = exportFilters.dateRange[1].endOf("day");
      result = result.filter((d) => {
        const date = dayjs(d.Date);
        return (date.isAfter(from) || date.isSame(from, "day")) &&
               (date.isBefore(to) || date.isSame(to, "day"));
      });
    }

    return result;
  };

  const getExportStats = (exportData) => {
    const empStats = {};
    let totalÜberstunden = 0;
    let totalMinusstunden = 0;
    let totalWorkedMin = 0;

    exportData.forEach((item) => {
      if (!empStats[item.user]) {
        empStats[item.user] = {
          überstunden: 0,
          minusstunden: 0,
          workDays: 0,
          absentDays: 0,
          gleittage: 0,
          totalWorkedMin: 0,
        };
      }
      const { überstunden, minusstunden, workedMin, type } = calculateOvertimeData(item);
      empStats[item.user].überstunden += überstunden;
      empStats[item.user].minusstunden += minusstunden;
      empStats[item.user].totalWorkedMin += workedMin;
      totalÜberstunden += überstunden;
      totalMinusstunden += minusstunden;
      totalWorkedMin += workedMin;

      if (type === "absent") {
        empStats[item.user].absentDays++;
      } else if (type === "gleittag" || type === "urlaub-uberstunden") {
        empStats[item.user].gleittage++;
        empStats[item.user].absentDays++;
      } else if (type !== "no-data") {
        empStats[item.user].workDays++;
      }
    });

    Object.keys(empStats).forEach((emp) => {
      empStats[emp].netBalance = empStats[emp].überstunden - empStats[emp].minusstunden;
    });

    return {
      empStats,
      totalÜberstunden,
      totalMinusstunden,
      netBalance: totalÜberstunden - totalMinusstunden,
      totalWorkedMin,
    };
  };

  const getStatus = (totalTime, record) => {
    if (record?.AbwesendType === "urlaub-uberstunden")
      return "Überstunden-Urlaub";
    if (record?.AbwesendType === "gleittag") return "Gleittag (Abbau)";
    if (record?.AbwesendType === "urlaub") return "Urlaub";
    if (record?.AbwesendType === "krank") return "Krank";
    if (totalTime === "Abwesend") return "Abwesend";
    if (totalTime === "Arbeitet noch") return "Arbeitet noch";
    if (totalTime === "Unvollständig") return "Unvollständig";
    if (totalTime === "Ungültige Zeit") return "Ungültig";
    return "Anwesend";
  };

  // CRUD Operations
  const handleEditClick = (record) => {
    setCurrentRecord(record);
    setFormData({
      user: record.user,
      StartTime: record.StartTime,
      endTime: record.endTime,
      Date: dayjs(record.Date).format("YYYY-MM-DD"),
      type: record.AbwesendType || (record.StartTime ? "work" : "abwesend"),
    });
    setOpenModal(true);
  };

  const handleAddClick = () => {
    setCurrentRecord(null);
    setFormData({
      user: "",
      StartTime: "",
      endTime: "",
      Date: dayjs().format("YYYY-MM-DD"),
      type: "work",
    });
    setOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenAddModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentRecord) {
        // Update logic remains same, just ensure fields are correct
        // ... (existing update logic) ...
        const updateData = {
          ...formData,
          AbwesendType:
            formData.type === "work"
              ? null
              : formData.type === "abwesend"
                ? null
                : formData.type,
            StartTime: formData.type === "work" ? formData.StartTime : "",
            endTime: formData.type === "work" ? formData.endTime : "",
        };

        await axios.put(
          `${base_url}/v1/updateAttendancebyID/${currentRecord._id}`,
          updateData,
        );
        const updatedData = data.map((item) =>
          item._id === currentRecord._id ? { ...item, ...updateData } : item,
        );
        setData(updatedData);
        showSnackbar("Eintrag erfolgreich aktualisiert");
      } else {
        // Validation for Overtime Holiday
        if (formData.type === "urlaub-uberstunden") {
          const userStats = statistics.employeeStats[formData.user];

          if (!userStats) {
            showSnackbar(
              "Keine Statistiken für diesen Mitarbeiter gefunden.",
              "error",
            );
            return;
          }

          const uberMinutes = userStats.uberstunden
            ? userStats.uberstunden.asMinutes()
            : 0;
          const minusMinutes = userStats.minusstunden
            ? userStats.minusstunden.asMinutes()
            : 0;
          const netBalance = dayjs.duration(
            uberMinutes - minusMinutes,
            "minutes",
          );

          // Check if balance is less than 8 hours
          if (netBalance.asHours() < 8) {
            showSnackbar(
              `Überstunden-Urlaub nicht möglich! Guthaben: ${formatDuration(netBalance)} (Benötigt: > 8h)`,
              "error",
            );
            return;
          }
        }

        // Create Logic
        if (formData.type === "work") {
          const response = await axios.post(
            `${base_url}/v1/createAttendanceDashboard`,
            formData,
          );
          setData((prevData) => [
            ...prevData,
            response.data.data || response.data,
          ]);
        } else {
          // New consolidated logic using createAttendanceDashboard
          const payload = {
            user: formData.user,
            Date: formData.Date,
            Abwesend: true,
            AbwesendType: formData.type,
            // Ensure StartTime/endTime are empty for absence types
            StartTime: "",
            endTime: "",
          };

          const response = await axios.post(
            `${base_url}/v1/createAttendanceDashboard`,
            payload,
          );
          setData((prevData) => [
            ...prevData,
            response.data.data || response.data,
          ]);
        }
        showSnackbar("Eintrag erfolgreich hinzugefügt");
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving attendance:", error);
      showSnackbar("Fehler beim Speichern", "error");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm("Möchten Sie diesen Eintrag wirklich löschen?")) {
      try {
        await axios.delete(`${base_url}/v1/deleteAttendance/${recordId}`);
        setData(data.filter((item) => item._id !== recordId));
        showSnackbar("Eintrag erfolgreich gelöscht");
      } catch (error) {
        console.error("Error deleting attendance:", error);
        showSnackbar("Fehler beim Löschen", "error");
      }
    }
  };

  // Components
  const StatCard = ({ title, value, icon, color, trend }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ color }}>
              {value}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend > 0 ? (
                  <TrendingUp sx={{ color: "success.main", fontSize: 16 }} />
                ) : (
                  <TrendingDown sx={{ color: "error.main", fontSize: 16 }} />
                )}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {Math.abs(trend)}% vs. letzter Monat
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.3 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const FilterPanel = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">
          <FilterList sx={{ verticalAlign: "middle", mr: 1 }} />
          Filter
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              employees: [],
              dateRange: [null, null],
              filterType: "all",
              quickFilter: "",
            });
          }}
          startIcon={<Clear />}
        >
          Filter zurücksetzen
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Mitarbeiter</InputLabel>
            <Select
              multiple
              value={filters.employees}
              onChange={(e) =>
                setFilters({ ...filters, employees: e.target.value })
              }
              input={<OutlinedInput label="Mitarbeiter" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Alle Mitarbeiter"
                  : `${selected.length} ausgewählt`
              }
            >
              <MenuItem
                value="all"
                onClick={() => {
                  const employeeList = getEmployeeList();
                  setFilters({
                    ...filters,
                    employees:
                      filters.employees.length === employeeList.length
                        ? []
                        : [...employeeList],
                  });
                }}
              >
                <Checkbox
                  checked={
                    filters.employees.length === getEmployeeList().length
                  }
                />
                <ListItemText primary="Alle auswählen" />
              </MenuItem>
              <Divider />
              {getEmployeeList().map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={filters.employees.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <ToggleButtonGroup
            value={filters.quickFilter}
            exclusive
            onChange={(e, value) => value && applyQuickFilter(value)}
            fullWidth
          >
            <ToggleButton value="today">Heute</ToggleButton>
            <ToggleButton value="week">Diese Woche</ToggleButton>
            <ToggleButton value="month">Dieser Monat</ToggleButton>
            <ToggleButton value="lastMonth">Letzter Monat</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <ToggleButtonGroup
            value={filters.filterType}
            exclusive
            onChange={(e, value) =>
              setFilters({ ...filters, filterType: value || "all" })
            }
            fullWidth
          >
            <ToggleButton value="all">Alle</ToggleButton>
            <ToggleButton value="abwesend">Abwesend</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2}>
              <DatePicker
                label="Von"
                value={filters.dateRange[0]}
                onChange={(newValue) =>
                  setFilters({
                    ...filters,
                    dateRange: [newValue, filters.dateRange[1]],
                    quickFilter: "",
                  })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="Bis"
                value={filters.dateRange[1]}
                onChange={(newValue) =>
                  setFilters({
                    ...filters,
                    dateRange: [filters.dateRange[0], newValue],
                    quickFilter: "",
                  })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Paper>
  );

  if (loading || employeesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            fetchData();
            fetchEmployees();
          }}
          startIcon={<Refresh />}
        >
          Erneut versuchen
        </Button>
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Keine Mitarbeiter gefunden. Bitte fügen Sie zuerst Mitarbeiter hinzu.
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            fetchEmployees();
          }}
          startIcon={<Refresh />}
        >
          Mitarbeiter neu laden
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          <Dashboard sx={{ verticalAlign: "middle", mr: 1 }} />
          Anwesenheitsverwaltung
        </Typography>
        <Box>
          <Tooltip title="Daten aktualisieren">
            <IconButton
              onClick={() => {
                fetchData();
                fetchEmployees();
              }}
              sx={{ mr: 1 }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleAddClick}
            sx={{ mr: 1 }}
          >
            Neuer Eintrag
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => setExportDialogOpen(true)}
          >
            Exportieren
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={(e, v) => setSelectedTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab label="Dashboard" icon={<Dashboard />} iconPosition="start" />
        <Tab label="Tabelle" icon={<Assessment />} iconPosition="start" />
        <Tab
          label="Mitarbeiterstatistiken"
          icon={<Group />}
          iconPosition="start"
        />
      </Tabs>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Gesamte Arbeitszeit"
                value={formatDuration(statistics.totalWorkingHours)}
                icon={<Schedule sx={{ fontSize: 40 }} />}
                color="primary.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Durchschn. Arbeitszeit"
                value={formatDuration(statistics.averageWorkingHours) || "N/A"}
                icon={<Schedule sx={{ fontSize: 40 }} />}
                color="info.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Abwesenheitstage"
                value={statistics.absentDays}
                icon={<Person sx={{ fontSize: 40 }} />}
                color="warning.main"
              />
            </Grid>
          </Grid>

          <FilterPanel />

          <MaterialReactTable
            columns={[
              {
                accessorKey: "user",
                header: "Mitarbeiter",
                Cell: ({ cell }) => (
                  <Chip
                    icon={<Person />}
                    label={cell.getValue()}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ),
              },
              {
                accessorKey: "Date",
                header: "Datum",
                Cell: ({ cell }) => {
                  const date = dayjs(cell.getValue());
                  const holiday = isHoliday(cell.getValue());
                  return (
                    <Box>
                      <Typography variant="body2">
                        {date.format("DD.MM.YYYY")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {date.format("dddd")}
                        {holiday && ` - ${holiday}`}
                      </Typography>
                    </Box>
                  );
                },
              },
              {
                accessorKey: "StartTime",
                header: "Startzeit",
                Cell: ({ cell }) => cell.getValue() || "-",
              },
              {
                accessorKey: "endTime",
                header: "Endzeit",
                Cell: ({ cell }) => getCappedEndTime(cell.getValue()) || "-",
              },
              {
                accessorKey: "totalTime",
                header: "Arbeitszeit",
                Cell: ({ row }) => {
                  const totalTime = calculateTotalTime(
                    row.original.StartTime,
                    row.original.endTime,
                    row.original.Date,
                  );
                  const formatted = formatDuration(totalTime);
                  const isWeekendWork = isWeekend(row.original.Date);

                  let color = "inherit";
                  if (formatted === "Abwesend") color = "error.main";
                  else if (formatted === "Arbeitet noch") color = "info.main";
                  else if (dayjs.isDuration(totalTime)) {
                    if (isWeekendWork) {
                      // Weekend work is always shown in success color (green) as it's overtime
                      color = "success.main";
                    } else if (totalTime.asHours() > 8.33) {
                      color = "success.main";
                    } else if (totalTime.asHours() < 6.75) {
                      color = "error.main";
                    }
                  }

                  return (
                    <Typography sx={{ color, fontWeight: "medium" }}>
                      {formatted}
                    </Typography>
                  );
                },
              },
              {
                accessorKey: "actions",
                header: "Aktionen",
                Cell: ({ row }) => (
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(row.original)}
                      color="primary"
                    >
                      <Settings />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRecord(row.original._id)}
                      color="error"
                    >
                      <Clear />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            data={filteredData}
            initialState={{
              pagination: { pageIndex: 0, pageSize: 10 },
              sorting: [{ id: "Date", desc: true }],
            }}
            enablePagination
            enableSorting
            enableColumnFilter
            enableColumnOrdering
            enableGrouping
            enableRowSelection
            muiTableProps={{
              sx: {
                tableLayout: "fixed",
              },
            }}
          />
        </>
      )}

      {selectedTab === 1 && (
        <>
          <FilterPanel />
          <MaterialReactTable
            columns={[
              // Same columns as above but with more detail
              {
                accessorKey: "user",
                header: "Mitarbeiter",
              },
              {
                accessorKey: "Date",
                header: "Datum",
                Cell: ({ cell }) => dayjs(cell.getValue()).format("DD.MM.YYYY"),
              },
              {
                accessorKey: "dayOfWeek",
                header: "Wochentag",
                Cell: ({ row }) => dayjs(row.original.Date).format("dddd"),
              },
              {
                accessorKey: "StartTime",
                header: "Startzeit",
              },
              {
                accessorKey: "endTime",
                header: "Endzeit",
                Cell: ({ cell }) => getCappedEndTime(cell.getValue()) || "-",
              },
              {
                accessorKey: "totalTime",
                header: "Arbeitszeit",
                Cell: ({ row }) =>
                  formatDuration(
                    calculateTotalTime(
                      row.original.StartTime,
                      row.original.endTime,
                      row.original.Date,
                    ),
                  ),
              },
              {
                accessorKey: "status",
                header: "Status",
                Cell: ({ row }) => {
                  const totalTime = calculateTotalTime(
                    row.original.StartTime,
                    row.original.endTime,
                    row.original.Date,
                  );
                  return getStatus(totalTime, row.original);
                },
              },
              {
                accessorKey: "actions",
                header: "Aktionen",
                Cell: ({ row }) => (
                  <Box>
                    <Button
                      size="small"
                      onClick={() => handleEditClick(row.original)}
                    >
                      Bearbeiten
                    </Button>
                  </Box>
                ),
              },
            ]}
            data={filteredData}
            enableFullScreenToggle
            enableGlobalFilter
          />
        </>
      )}

      {selectedTab === 2 && (
        <>
          <FilterPanel />
          <Grid container spacing={3}>
            {Object.entries(statistics.employeeStats).map(([employee, stats]) => (
              <Grid item xs={12} md={6} key={employee}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Person sx={{ verticalAlign: "middle", mr: 1 }} />
                      {employee}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Arbeitstage
                        </Typography>
                        <Typography variant="h6">
                          {stats.workDays} / {stats.totalDays}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Gesamtstunden
                        </Typography>
                        <Typography variant="h6">
                          {formatDuration(stats.totalHours)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Abwesenheitstage
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                          {stats.absentDays}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openModal || openAddModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentRecord ? "Eintrag bearbeiten" : "Neuer Eintrag"}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Mitarbeiter</InputLabel>
                  <Select
                    value={formData.user}
                    onChange={handleChange}
                    name="user"
                    label="Mitarbeiter"
                  >
                    {getEmployeeList().map((employee) => (
                      <MenuItem key={employee} value={employee}>
                        {employee}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Datum"
                  type="date"
                  name="Date"
                  value={formData.Date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={handleChange}
                    name="type"
                    label="Typ"
                  >
                    <MenuItem value="work">Arbeit</MenuItem>
                    <MenuItem value="gleittag">
                      Gleittag (Überstundenabbau)
                    </MenuItem>
                    <MenuItem value="urlaub-uberstunden">
                      Überstunden-Urlaub ({">"} 8h Guthaben)
                    </MenuItem>
                    <MenuItem value="urlaub">Urlaub</MenuItem>
                    <MenuItem value="krank">Krank</MenuItem>
                    <MenuItem value="abwesend">Sonstiges / Abwesend</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.type === "work" && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Startzeit"
                      type="time"
                      name="StartTime"
                      value={formData.StartTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Endzeit"
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Abbrechen</Button>
          <Button onClick={handleSubmit} variant="contained">
            {currentRecord ? "Speichern" : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FileDownload sx={{ mr: 1 }} />
            Professioneller Export — Überstunden & Minusstunden
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>

            {/* Format selector */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={exportOptions.format === "excel" ? 3 : 1}
                sx={{
                  p: 2, cursor: "pointer",
                  border: exportOptions.format === "excel" ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  transition: "all 0.2s",
                }}
                onClick={() => setExportOptions({ ...exportOptions, format: "excel" })}
              >
                <Box display="flex" alignItems="center" mb={0.5}>
                  <FileDownload sx={{ mr: 1, color: "success.main" }} />
                  <Typography variant="h6">Excel Export (.xlsx)</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Mehrere Arbeitsblätter mit Überstunden/Minusstunden-Analyse
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li><Typography variant="caption">Haupttabelle mit Überstunden- & Minusstunden-Spalten</Typography></li>
                  <li><Typography variant="caption">Dediziertes Überstunden-Analyse-Blatt</Typography></li>
                  <li><Typography variant="caption">Mitarbeiter-Nettosaldo-Übersicht</Typography></li>
                  <li><Typography variant="caption">Optionale individuelle Mitarbeiterblätter</Typography></li>
                </ul>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={exportOptions.format === "pdf" ? 3 : 1}
                sx={{
                  p: 2, cursor: "pointer",
                  border: exportOptions.format === "pdf" ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  transition: "all 0.2s",
                }}
                onClick={() => setExportOptions({ ...exportOptions, format: "pdf" })}
              >
                <Box display="flex" alignItems="center" mb={0.5}>
                  <FileDownload sx={{ mr: 1, color: "error.main" }} />
                  <Typography variant="h6">PDF Export</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Druckfertiger Bericht mit farbiger Bilanzübersicht
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li><Typography variant="caption">Farbcodierte Überstunden (grün) & Minusstunden (rot)</Typography></li>
                  <li><Typography variant="caption">Nettosaldo-Karte pro Mitarbeiter</Typography></li>
                  <li><Typography variant="caption">Professioneller Header mit Zeitraum</Typography></li>
                  <li><Typography variant="caption">Seitennummerierung & Zeitstempel</Typography></li>
                </ul>
              </Paper>
            </Grid>

            {/* Filter section */}
            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Export-Filter (unabhängig von der Seitenanzeige)
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Mitarbeiter auswählen</InputLabel>
                <Select
                  multiple
                  value={exportFilters.employees}
                  onChange={(e) => setExportFilters({ ...exportFilters, employees: e.target.value })}
                  input={<OutlinedInput label="Mitarbeiter auswählen" />}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? "Alle Mitarbeiter"
                      : `${selected.length} ausgewählt`
                  }
                >
                  <MenuItem
                    onClick={() => {
                      const all = getEmployeeList();
                      setExportFilters({
                        ...exportFilters,
                        employees: exportFilters.employees.length === all.length ? [] : all,
                      });
                    }}
                  >
                    <Checkbox checked={exportFilters.employees.length === getEmployeeList().length} />
                    <ListItemText primary="Alle auswählen" />
                  </MenuItem>
                  <Divider />
                  {getEmployeeList().map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={exportFilters.employees.indexOf(name) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={7}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" gap={2}>
                  <DatePicker
                    label="Von"
                    value={exportFilters.dateRange[0]}
                    onChange={(v) => setExportFilters({ ...exportFilters, dateRange: [v, exportFilters.dateRange[1]] })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                  <DatePicker
                    label="Bis"
                    value={exportFilters.dateRange[1]}
                    onChange={(v) => setExportFilters({ ...exportFilters, dateRange: [exportFilters.dateRange[0], v] })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                  <Tooltip title="Filter zurücksetzen">
                    <IconButton
                      size="small"
                      onClick={() => setExportFilters({ employees: [], dateRange: [null, null] })}
                    >
                      <Clear />
                    </IconButton>
                  </Tooltip>
                </Box>
              </LocalizationProvider>
            </Grid>

            {/* Options */}
            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Export-Optionen
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.includeOvertimeAnalysis}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeOvertimeAnalysis: e.target.checked })}
                    color="warning"
                  />
                }
                label="Überstunden-Analyse"
              />
              <Typography variant="caption" display="block" sx={{ ml: 4 }} color="textSecondary">
                Dediziertes Blatt / Seite mit Über- & Minusstunden
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.includeSummary}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeSummary: e.target.checked })}
                  />
                }
                label="Zusammenfassung"
              />
              <Typography variant="caption" display="block" sx={{ ml: 4 }} color="textSecondary">
                Gesamtübersicht mit Nettosaldo
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.groupByEmployee}
                    onChange={(e) => setExportOptions({ ...exportOptions, groupByEmployee: e.target.checked })}
                  />
                }
                label="Nach Mitarbeiter gruppieren"
              />
              <Typography variant="caption" display="block" sx={{ ml: 4 }} color="textSecondary">
                Separate Blätter/Abschnitte
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.includeStatistics}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeStatistics: e.target.checked })}
                  />
                }
                label="Detaillierte Statistiken"
              />
              <Typography variant="caption" display="block" sx={{ ml: 4 }} color="textSecondary">
                Zusätzliches Statistikblatt
              </Typography>
            </Grid>

            {/* Live Preview */}
            <Grid item xs={12}>
              {(() => {
                const previewData = getExportFilteredData();
                const previewStats = getExportStats(previewData);
                const net = previewStats.netBalance;
                return (
                  <Paper sx={{ p: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Export-Vorschau
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Einträge</Typography>
                        <Typography variant="body2" fontWeight="bold">{previewData.length}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Zeitraum</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {exportFilters.dateRange[0] && exportFilters.dateRange[1]
                            ? `${exportFilters.dateRange[0].format("DD.MM")} – ${exportFilters.dateRange[1].format("DD.MM.YY")}`
                            : "Alle Daten"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Mitarbeiter</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {exportFilters.employees.length > 0 ? exportFilters.employees.length : Object.keys(previewStats.empStats).length}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Gesamtarbeitszeit</Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {formatMinutes(previewStats.totalWorkedMin)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Überstunden</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          + {formatMinutes(previewStats.totalÜberstunden)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Minusstunden</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          − {formatMinutes(previewStats.totalMinusstunden)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Nettosaldo</Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={net >= 0 ? "success.main" : "error.main"}
                        >
                          {net >= 0 ? "+" : ""}{formatMinutes(Math.abs(net))}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Standard / Tag</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatMinutes(STANDARD_MINUTES_PER_DAY)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={exportData}
            variant="contained"
            startIcon={<Download />}
            disabled={!exportOptions.format}
          >
            {exportOptions.format === "excel" ? "Excel exportieren" : "PDF exportieren"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Attendance;
